import React, { JSX } from 'react';
import joinClassNames from 'web/utils/joinClassNames';

// TODO: can we reuse this function for both intrinsics and components?
const intrinsicElement = <P extends keyof JSX.IntrinsicElements>(
  component: P,
  styledClassName?: string,
  styledStyle?: React.CSSProperties,
): React.ForwardRefExoticComponent<
  React.PropsWithoutRef<JSX.IntrinsicElements[P]> & React.RefAttributes<HTMLElement | SVGElement>
> =>
  // eslint-disable-next-line react/display-name
  React.forwardRef<HTMLElement | SVGElement, JSX.IntrinsicElements[P]>((props, ref) => {
    const { children, className, style, ...restProps } = props as JSX.IntrinsicElements[P] & {
      children?: React.ReactNode;
      className?: string;
      style?: React.CSSProperties;
    };
    return React.createElement(
      component,
      {
        ...restProps,
        className: joinClassNames(styledClassName, className),
        style: { ...styledStyle, ...style },
        ref,
      },
      children,
    );
  });

type StyledProxy = {
  [K in keyof JSX.IntrinsicElements]: (
    className?: string,
    style?: React.CSSProperties,
  ) => React.FunctionComponent<JSX.IntrinsicElements[K]>;
};

type StyleableProps = { children?: React.ReactNode; className?: string; style?: React.CSSProperties };
type StyledFunction = {
  <P>(
    component: React.ComponentType<P & { className?: string; style?: React.CSSProperties }>,
  ): (className?: string, style?: React.CSSProperties) => React.ForwardRefExoticComponent<P>;
};

type Styled = StyledFunction & StyledProxy;

const sc = new Proxy(
  (<P extends StyleableProps>(component: React.ComponentType<P>) =>
    (styledClassName?: string, styledStyle?: React.CSSProperties) =>
      // eslint-disable-next-line react/display-name
      React.forwardRef<unknown, P>((props, ref) => {
        const { className, style, children, ...restProps } = props as P & StyleableProps;
        return React.createElement(
          component,
          {
            ...restProps,
            className: joinClassNames(styledClassName, className),
            ...((styledStyle || style) && { style: { ...styledStyle, ...style } }),
            ref,
          } as unknown as P,
          children,
        );
      })) as StyledFunction,
  {
    get: (_, name: keyof JSX.IntrinsicElements) => (className?: string, style?: React.CSSProperties) =>
      intrinsicElement(name, className, style),
  },
) as Styled;

export default sc;
